import React, { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  MenuItem,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AppState } from '../../../../state/configureStore';
import { TaskItemState } from '../../../../state/taskItem';
import { useSelector, useDispatch } from 'react-redux';
import { TaskItemChooseDialog, OpportunityLinkButton } from '../../../components/';
import {
  createTaskSubtaskSubtaskItemAndEtc,
  addSelectedTaskItemInTaskCreate,
  addSelectedTaskItemMoqInTaskCreate,
  TaskCreateState,
  addSelectedTaskItemForecastInTaskCreate,
  addSelectedUnitOfQuantityInTaskCreate,
} from '../../../../state/taskCreate';
import { SubtaskTypeState } from '../../../../state/subtaskType';
import { ISubtaskType } from '../../../../models/subtaskType.model';
import UnitOfQuantities, {
  UnitOfQuantityLabel,
} from '../../../../constants/unitOfQuantity';
import {
  Close as CloseIcon,
  SkipNext as NextIcon,
  SkipPrevious as PreviousIcon,
  Search as SearchIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    titleContent: {
      padding: theme.spacing(2.25, 3, 1.75),
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    titleText: {
      flex: 1,
      minWidth: 0,
    },
    titleHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.84)',
      lineHeight: 1.65,
      maxWidth: 760,
    },
    titleActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.9, 1.5),
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.14)',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
      color: '#ffffff',
      fontSize: '0.82rem',
      fontWeight: 700,
      backdropFilter: 'blur(8px)',
    },
    closeButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.12)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
      },
    },
    content: {
      padding: theme.spacing(2, 3, 2),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(255,255,255,1) 100%)',
    },
    filterSurface: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1.75),
      borderRadius: 20,
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    filterMeta: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      minWidth: 0,
    },
    metaTitle: {
      fontWeight: 700,
      color: '#0f172a',
    },
    metaSubtitle: {
      color: '#64748b',
      lineHeight: 1.6,
    },
    controlRow: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    selectItemButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    formControl: {
      minWidth: 220,
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    selectInput: {
      backgroundColor: '#ffffff',
      borderRadius: 14,
    },
    tableMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    countBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.85, 1.5),
      borderRadius: 999,
      fontSize: '0.82rem',
      fontWeight: 700,
      color: '#1d4ed8',
      background: 'rgba(219, 234, 254, 0.9)',
      border: '1px solid rgba(96, 165, 250, 0.35)',
    },
    tableSurface: {
      borderRadius: 12,
      overflow: 'hidden',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
      marginTop: theme.spacing(2),
    },
    tableScrollArea: {
      overflowX: 'auto',
    },
    table: {
      minWidth: 700,
      marginTop: 0,
    },
    tableHeadRow: {
      background: 'rgba(241, 245, 249, 0.9)',
    },
    tableHeadCell: {
      borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
      color: '#ffffff',
      fontWeight: 800,
      fontSize: '0.78rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
    },
    resultRow: {
      '&:not(:last-child) td': {
        borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
      },
      '&:hover': {
        background: 'rgba(248, 250, 252, 0.85)',
      },
    },
    indexCell: {
      width: 70,
      fontWeight: 700,
      color: '#334155',
      whiteSpace: 'nowrap',
    },
    actions: {
      padding: theme.spacing(2, 3, 2),
      justifyContent: 'space-between',
      alignItems: 'center',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.96) 40%)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
      },
    },
    actionText: {
      color: '#64748b',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      maxWidth: 460,
    },
    actionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    secondaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    primaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 55%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)',
        boxShadow: '0 16px 34px rgba(37, 99, 235, 0.28)',
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.72)',
        background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)',
      },
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
  }),
);

interface TaskItemListDialogProps {
  nextClick: () => void;
  backClick: () => void;
  finishClick: () => void;
  closeClick: () => void;
}

const TaskItemListDialog: React.FC<TaskItemListDialogProps> = (props) => {
  const classes = useStyles();
  const { nextClick, backClick, finishClick, closeClick } = props;
  const { selectedUnitOfQuantity } = useSelector<AppState, TaskCreateState>(
    (state) => state.taskCreate,
  );
  const [unitOfQuantity, setUnitOfQuantity] = useState(selectedUnitOfQuantity);
  const dispatch = useDispatch();

  const [openTaskItemChooseDialog, setOpenTaskItemChooseDialog] = useState(false);
  const { selectedItems } = useSelector<AppState, TaskItemState>(
    (state) => state.taskItem,
  );
  const {
    createSubtasksPayload,
    selectedTaskItemsWithMoqs,
    selectedTaskItemsWithForecasts,
  } = useSelector<AppState, TaskCreateState>((state) => state.taskCreate);
  const { items: subtaskTypeList } = useSelector<AppState, SubtaskTypeState>(
    (state) => state.subtaskType,
  );

  const addTaskItem = () => {
    setOpenTaskItemChooseDialog(true);
  };

  const handleNextClick = () => {
    dispatch(addSelectedTaskItemInTaskCreate(selectedItems));

    const priceSubtaskType = subtaskTypeList.find(
      (subtaskType: ISubtaskType) => subtaskType.code === 'PRICE',
    );
    const subtaskTypePriceId = priceSubtaskType?.id;

    if (
      subtaskTypePriceId &&
      createSubtasksPayload.findIndex(
        (createSubtask) => createSubtask.smSubtaskTypeId === subtaskTypePriceId,
      ) !== -1
    ) {
      const selectedItemsWithMoq = selectedItems.map((selectedItem) => {
        const selectedOpportunityItemId = selectedItem.opportunityItemId;
        const foundSelectedTaskItemWithMoqs = selectedTaskItemsWithMoqs.find(
          (row) =>
            selectedOpportunityItemId !== undefined &&
            row.opportunityItemId === selectedOpportunityItemId,
        );
        if (foundSelectedTaskItemWithMoqs) {
          return foundSelectedTaskItemWithMoqs;
        }
        return { ...selectedItem, moqs: [] };
      });

      const selectedItemsWithForecast = selectedItems.map((selectedItem) => {
        const selectedOpportunityItemId = selectedItem.opportunityItemId;
        const foundSelectedTaskItemWithForecasts = selectedTaskItemsWithForecasts.find(
          (row) =>
            selectedOpportunityItemId !== undefined &&
            row.opportunityItemId === selectedOpportunityItemId,
        );
        if (foundSelectedTaskItemWithForecasts) {
          return foundSelectedTaskItemWithForecasts;
        }
        return { ...selectedItem, forecasts: [] };
      });

      dispatch(addSelectedTaskItemMoqInTaskCreate(selectedItemsWithMoq));
      dispatch(addSelectedTaskItemForecastInTaskCreate(selectedItemsWithForecast));
      dispatch(addSelectedUnitOfQuantityInTaskCreate(unitOfQuantity));

      nextClick();
    } else {
      dispatch(createTaskSubtaskSubtaskItemAndEtc());
      dispatch(addSelectedUnitOfQuantityInTaskCreate(unitOfQuantity));
      finishClick();
    }
  };
  const disabledNextButton = selectedItems.length === 0 || unitOfQuantity === '';
  return (
    <>
      <TaskItemChooseDialog
        open={openTaskItemChooseDialog}
        handleClose={() => {
          setOpenTaskItemChooseDialog(false);
        }}
      />
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleContent}>
          <Box className={classes.titleText}>
            <Typography variant="h4" className={classes.titleHeading}>
              Task Item & Unit of Quantity
            </Typography>
            <Typography variant="body1" className={classes.titleSubtitle}>
              Select the task items you want to include and define the shared unit of
              quantity before moving to the next step.
            </Typography>
          </Box>
          <Box className={classes.titleActions}>
            <Box className={classes.titleBadge}>{selectedItems.length} selected</Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                closeClick();
              }}
              className={classes.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.controlRow}>
          <Button
            startIcon={<SearchIcon />}
            color="primary"
            variant="contained"
            onClick={addTaskItem}
            className={classes.selectItemButton}
          >
            Select Item
          </Button>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Unit of Quantity</InputLabel>
            <Select
              value={unitOfQuantity}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setUnitOfQuantity(event.target.value as string);
              }}
              inputProps={{ name: 'unitOfQuantity' }}
              label="Unit of Quantity"
              className={classes.selectInput}
              margin="dense"
            >
              {Object.entries(UnitOfQuantities).map(([key, value]) => {
                return (
                  <MenuItem key={key} value={value}>
                    {UnitOfQuantityLabel[value]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.tableSurface}>
          <Box className={classes.tableScrollArea}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell className={classes.tableHeadCell}>No.</TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Assignment Number
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>Program</TableCell>
                  <TableCell className={classes.tableHeadCell}>Brand</TableCell>
                  <TableCell className={classes.tableHeadCell}>Block</TableCell>
                  <TableCell className={classes.tableHeadCell}>Item</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedItems.map((item, index) => {
                  const opportunityItem = item.opportunityItem;
                  const block = opportunityItem?.block;
                  const opportunity = opportunityItem?.opportunity;
                  const rowKey =
                    item.opportunityItemId || `${block?.blockNo || 'item'}-${index}`;

                  return (
                    <TableRow key={rowKey} className={classes.resultRow}>
                      <TableCell className={classes.indexCell}>{index + 1}</TableCell>
                      <TableCell>
                        {opportunity ? (
                          <OpportunityLinkButton opportunity={opportunity} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{block?.program?.name || '-'}</TableCell>
                      <TableCell>{block?.brand?.name || '-'}</TableCell>
                      <TableCell>{block?.blockNo || '-'}</TableCell>
                      <TableCell>{block?.item || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Typography className={classes.actionText}>
          Make sure at least one item is selected and the unit of quantity is set before
          continuing.
        </Typography>
        <Box>
          <Button
            startIcon={<PreviousIcon />}
            color="primary"
            variant="outlined"
            onClick={backClick}
            className={classes.secondaryButton}
          >
            Back
          </Button>
          <Button
            onClick={handleNextClick}
            variant="contained"
            color="primary"
            startIcon={<NextIcon />}
            disabled={disabledNextButton}
            className={classes.primaryButton}
          >
            Next
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default TaskItemListDialog;
